import { useEffect, useState, useCallback } from "react";
import { X, Search, Trash2, MapPin, Lock } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panneau admin de rattachement de parcelles.
 * - Sans client sélectionné : liste/recherche des utilisateurs.
 * - Avec un client : rappel + liste des parcelles rattachées (retrait possible).
 *
 * Le clic sur les parcelles de la carte est géré par le parent (MapDashboard) ;
 * ce composant gère le choix du client et la liste des parcelles déjà rattachées.
 */
export default function AdminAttachPanel({ targetUser, onSelectUser, onExit, refreshSignal, onChanged }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [parcels, setParcels] = useState([]);
  const [loadingParcels, setLoadingParcels] = useState(false);

  // Liste des utilisateurs pour le sélecteur (tant qu'aucun client n'est choisi)
  useEffect(() => {
    if (targetUser) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const res = await authFetch(`${API_BASE}/admin/all-users`);
        const data = await res.json();
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoadingUsers(false); }
    })();
    return () => { cancelled = true; };
  }, [targetUser]);

  // Parcelles rattachées au client courant
  const loadParcels = useCallback(async () => {
    if (!targetUser) { setParcels([]); return; }
    setLoadingParcels(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${targetUser.user_id}/parcels`);
      const data = await res.json();
      setParcels(data?.parcels || []);
    } catch { /* ignore */ }
    finally { setLoadingParcels(false); }
  }, [targetUser]);

  useEffect(() => { loadParcels(); }, [loadParcels, refreshSignal]);

  const handleRemove = async (pid) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/parcels/${pid}/lock`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Parcelle détachée");
        setParcels((prev) => prev.filter((p) => p.parcel_id !== pid));
        onChanged?.(pid, null);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(typeof d.detail === "string" ? d.detail : "Détachement impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
  };

  const initials = (u) => (u?.name || u?.email || "?").trim().slice(0, 2).toUpperCase();
  const totalHa = parcels.reduce((s, p) => s + (p.surface_ha || 0), 0);
  const filtered = users
    .filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 40);

  return (
    <div className="floating-panel h-full flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* En-tête */}
      <div className="px-4 py-3 text-white flex items-center gap-2" style={{ background: "#1f5c39" }}>
        <Lock className="w-4 h-4" />
        <span className="text-sm font-semibold">Mode rattachement</span>
        <button onClick={onExit} className="ml-auto text-white/90 hover:text-white" aria-label="Terminer">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!targetUser ? (
        /* -------- Sélection du client -------- */
        <div className="flex-1 flex flex-col min-h-0 p-3">
          <p className="text-xs text-gray-500 mb-2">À quel client rattacher des parcelles ?</p>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un client…"
              className="w-full text-sm pl-8 pr-2 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loadingUsers && <p className="text-xs text-gray-400 py-4 text-center">Chargement…</p>}
            {!loadingUsers && filtered.map((u) => (
              <button
                key={u.user_id}
                onClick={() => onSelectUser(u)}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-emerald-50 text-left"
              >
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center text-xs font-bold flex-none">{initials(u)}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-800 truncate">{u.name || u.email}</span>
                  <span className="block text-[11px] text-gray-400 truncate">{u.email}</span>
                </span>
              </button>
            ))}
            {!loadingUsers && filtered.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">Aucun client trouvé.</p>
            )}
          </div>
        </div>
      ) : (
        /* -------- Client sélectionné -------- */
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center text-xs font-bold flex-none">{initials(targetUser)}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-800 truncate">{targetUser.name || targetUser.email}</span>
              <span className="block text-[11px] text-gray-400 truncate">{targetUser.email}</span>
            </span>
            <button onClick={() => onSelectUser(null)} className="text-[11px] text-emerald-700 hover:underline flex-none">Changer</button>
          </div>
          <div className="px-4 py-2 bg-emerald-50 text-emerald-800 text-xs flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 flex-none" /> Cliquez sur les parcelles à rattacher.
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loadingParcels && <p className="text-xs text-gray-400 py-4 text-center">Chargement…</p>}
            {!loadingParcels && parcels.length === 0 && (
              <p className="text-xs text-gray-400 py-6 text-center leading-relaxed">Aucune parcelle rattachée pour l'instant.<br />Cliquez sur la carte.</p>
            )}
            {parcels.map((p) => (
              <div key={p.parcel_id} className="flex items-center gap-2 border border-gray-100 rounded-lg p-2">
                <span className="w-7 h-7 rounded bg-emerald-100 text-emerald-800 grid place-items-center flex-none"><Lock className="w-3.5 h-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-gray-800 truncate">{p.culture_type || p.name || "Parcelle"}</span>
                  <span className="block text-[11px] text-gray-400 truncate">{p.commune || ""}{p.surface_ha ? ` · ${p.surface_ha} ha` : ""}</span>
                </span>
                <button onClick={() => handleRemove(p.parcel_id)} className="text-gray-300 hover:text-red-500 flex-none" aria-label="Détacher">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {parcels.length} parcelle{parcels.length > 1 ? "s" : ""} · {totalHa.toFixed(1)} ha
            </span>
            <button onClick={onExit} className="text-sm font-semibold text-white px-3 py-1.5 rounded-lg" style={{ background: "#2e7d4e" }}>Terminer</button>
          </div>
        </div>
      )}
    </div>
  );
}
